import { useEffect, useState } from "react";
import type { OpenFormDescriptor } from "@/types/dynamic-detail";
import { DynamicObjectForm } from "./dynamic-object-form";
import { FormChooser, type OfferedForm } from "./form-chooser";

type OpenFormDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Title of the chooser, shown when several forms apply. */
    title: string;
    descriptor: OpenFormDescriptor;
    onSuccess?: () => void;
};

/**
 * The creation form(s) of another object, opened from a record (`DynamicAction.open_form`): the
 * chooser first when several forms apply, then the form itself, posting to the other object's
 * endpoints with the context of the record. Same component and same contract as the list page
 * opened with `?create=<form>&<context>`: `form-prefill` with the context, the write with it, and
 * `redirect_after_create` followed on success.
 */
export function OpenFormDialog({ open, onOpenChange, title, descriptor, onSuccess }: OpenFormDialogProps) {
    const offered: OfferedForm[] = descriptor.forms.map(({ index, config }) => ({ form: config, index }));
    const [chosen, setChosen] = useState<OfferedForm | null>(null);

    useEffect(() => {
        if (open) setChosen(offered.length === 1 ? offered[0] : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (offered.length === 0) return null;
    const endpointUrl = `/crm/objects/${descriptor.object_url}/`;
    return (
        <>
            {offered.length > 1 && (
                <FormChooser
                    open={open && chosen === null}
                    onOpenChange={next => {
                        if (!next) onOpenChange(false);
                    }}
                    title={title}
                    forms={offered}
                    onChoose={setChosen}
                />
            )}
            {chosen && (
                <DynamicObjectForm
                    key={chosen.index}
                    open={open}
                    onOpenChange={next => {
                        if (!next) {
                            setChosen(null);
                            onOpenChange(false);
                        }
                    }}
                    mode="create"
                    formConfig={chosen.form}
                    endpointUrl={endpointUrl}
                    formIndex={chosen.index}
                    formScope="list"
                    context={descriptor.context}
                    onSuccess={onSuccess}
                />
            )}
        </>
    );
}
